/**
 * Club Bundle Builder (ECSR-302)
 *
 * Vanilla custom element powering `sections/club-bundle-builder.liquid`. Lets a customer pick N scents
 * (N = data-bundle-size) from the rendered collection grid, then adds ONE unit of the club's Parent Bundle
 * Product to the cart with the chosen scents recorded as line item properties:
 *   - visible: "Scent 1".."Scent N" -> "{Scent Name} ({Size})" (shown in cart + on the admin order)
 *   - hidden ("_"-prefixed, never shown to the customer or on the admin order):
 *       _bundle_item          "true"  - lets line-item.liquid / cart-drawer.liquid branch on bundle lines
 *       _club_handle          the club/builder page identifier
 *       _builder_page_url     the builder page URL, used to build the cart "Edit" link
 *       _scent_variant_ids    comma-joined child variant IDs, used to rebuild the Edit pre-fill query string
 *       _bundle_uid           a unique id minted per add - see below
 *
 * ECSR-678 (client review): a shopper may add AS MANY bundles as they like from the same club, including
 * two bundles with the identical assortment. Two things make that work:
 *
 *   1. Every add mints a fresh `_bundle_uid`. Shopify's /cart/add.js merges an add into an existing line
 *      when the variant AND all line item properties match, so without the uid a repeat of the same
 *      assortment would land as quantity 2 on one line. A bundle line has no quantity stepper
 *      (snippets/line-item.liquid suppresses it) and carries a single "Edit bundle" link, so a
 *      quantity-2 bundle line could be neither decremented nor edited per bundle. The uid keeps each
 *      bundle its own line, independently editable and removable.
 *   2. Nothing is de-duplicated on a plain add. Until this change submit() looked up ANY cart line whose
 *      variant was this club's Parent Bundle Product and removed it first ("replace-not-duplicate", from
 *      ECSR-302), which is what made a second bundle silently overwrite the first. Replacement now only
 *      happens when the builder was opened from a specific cart line's Edit link, and only that line is
 *      replaced - matched on its `_bundle_uid`, never on the parent variant.
 *
 * The Edit link therefore carries `?bundle_edit=<variantIds>&bundle_uid=<uid>`. An Edit link from a cart
 * created before this change has no uid; such a line is instead resolved from the cart by its parent
 * variant plus the absence of a uid - unique, because the code being replaced enforced one bundle line per
 * club - so editing a cart that predates this release still REPLACES rather than adding a second charged
 * line. See findBundleLineToReplace().
 *
 * Child scent products/variants are NEVER added to the cart as their own line - only the Parent Bundle
 * Product's inventory ever decrements, per the ticket's confirmed decision.
 */

const EDIT_QUERY_PARAM = 'bundle_edit';
// ECSR-678: identifies WHICH bundle line an Edit link came from, so editing replaces that one line and
// leaves the shopper's other bundles alone.
const EDIT_UID_QUERY_PARAM = 'bundle_uid';
// The footer section wrapper: Stretch stamps `shopify-section--<type>` on every section wrapper (schema `class`).
const FOOTER_SELECTOR = '.shopify-section--footer, [id^="shopify-section-"][id$="__footer"]';

function dispatchCartRefresh() {
  document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
}

class ClubBundleBuilder extends HTMLElement {
  connectedCallback() {
    this.bundleSize = Math.max(1, parseInt(this.dataset.bundleSize, 10) || 1);
    this.allowDuplicates = this.dataset.allowDuplicates === 'true';
    this.parentVariantId = this.dataset.parentVariantId ? Number(this.dataset.parentVariantId) : null;
    this.clubHandle = this.dataset.clubHandle || '';
    this.builderPageUrl = this.dataset.builderPageUrl || window.location.pathname;

    this.grid = this.querySelector('[data-bundle-grid]');
    this.panel = this.querySelector('[data-bundle-panel]');
    this.listEl = this.querySelector('[data-bundle-list]');
    this.countEl = this.querySelector('[data-bundle-count]');
    this.messageEl = this.querySelector('[data-bundle-message]');
    this.errorEl = this.querySelector('[data-bundle-error]');
    // ECSR-678: lives outside the panel, so it survives the fly-out closing - see the markup.
    this.noticeEl = this.querySelector('[data-bundle-notice]');
    this.submitButton = this.querySelector('.club-bundle-builder__submit');
    this.panelCloseButton = this.querySelector('[data-bundle-panel-close]');
    this.panelTab = this.querySelector('[data-bundle-panel-tab]');

    // ECSR-683: mobile fly-out state. The panel slides up automatically when the bundle reaches the
    // required count; the X closes it and the fixed "Your Bundle" tab reopens it.
    this.mobileMedia = window.matchMedia('(max-width: 999px)');

    // Ordered list of { variantId, name, size } - order matches "Scent 1", "Scent 2", ... in cart properties.
    this.selections = [];
    // ECSR-678: set by prefillFromQueryString() when the shopper arrived from a cart line's Edit link.
    // A plain add (isEditing false) never replaces an existing bundle.
    this.isEditing = false;
    this.editBundleUid = null;
    this.isSubmitting = false;
    this.announceAddedOnNextRender = false;
    // ECSR-678: a specific message to announce instead of the generic added message, if any.
    this.announceOnNextRender = null;

    this.tabCountEl = this.querySelector('[data-bundle-tab-count]');
    this.liveRegion = this.querySelector('[data-bundle-live]');

    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);
    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('keydown', this.onKeydown);

    this.prefillFromQueryString();
    this.render();

    // ECSR-758 footer-aware tab: optional enhancement, wired AFTER core init so a failure here can never take
    // the builder itself down. The markup ships with `is-footer-unobserved` (keeping the pre-758 bottom
    // reserve for a no-JS page); observeFooter() removes it once the observer is actually installed.
    this.footerIntersecting = false;
    this.focusParkedFromTab = false;
    // ECSR-678: set only when focus was genuinely parked on the anchor from inside the open fly-out
    // (see syncFooterState) - never merely because the fly-out was opened.
    this.focusParkedFromPanel = false;
    // ECSR-678: an auto-open that arrived while the footer was in view, to be performed when it leaves.
    this.pendingFlyoutOpen = false;
    this.syncFooterState = this.syncFooterState.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);
    this.observeFooter();
    try {
      // A breakpoint change while parked at the footer (tablet rotation, window resize) produces no new
      // intersection entry, so re-evaluate the state on the media query itself.
      this.mobileMedia.addEventListener('change', this.syncFooterState);
    } catch (error) {
      // Older MediaQueryList implementations only expose addListener - non-critical, same as samples.js.
    }
    // The theme editor replaces a section's wrapper when it reloads that section; if the footer is reloaded
    // the observed node is orphaned and would never intersect again, so re-resolve it.
    document.addEventListener('shopify:section:load', this.onSectionLoad);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    try {
      this.mobileMedia.removeEventListener('change', this.syncFooterState);
    } catch (error) {
      // See connectedCallback - non-critical.
    }
    this.footerObserver?.disconnect();
    this.footerObserver = null;
  }

  onSectionLoad(event) {
    const loaded = event.target;
    if (loaded && loaded.matches?.(FOOTER_SELECTOR)) {
      this.observeFooter();
    }
  }

  /**
   * ECSR-758: the fixed "Your Bundle" tab must not float over (or past) the footer. Watch the footer section
   * and mark the builder while any part of it is in the viewport; CSS slides the tab away in that state and
   * back when the shopper scrolls up. The state is only ever set at the mobile breakpoint (see the callback),
   * matching the tab, which is display: none on desktop. Nothing in the flow changes height, so the page
   * never jumps.
   *
   * Known limit: on a club page short enough for the footer to be in view at every scroll position the tab
   * would stay hidden and the fly-out could only be reopened by adding a scent. None of the four club pages
   * is anywhere near that short (each is several screens of cards at 375px).
   */
  observeFooter() {
    this.footerObserver?.disconnect();
    this.footerObserver = null;

    const footer = document.querySelector(FOOTER_SELECTOR);
    if (!footer || !('IntersectionObserver' in window)) {
      // Keep (or restore) the pre-ECSR-758 bottom reserve when nothing is driving the tab.
      this.classList.add('is-footer-unobserved');
      this.footerIntersecting = false;
      this.syncFooterState();
      return;
    }

    // The observer is live from here, so the server-rendered reserve can go.
    this.classList.remove('is-footer-unobserved');
    this.footerObserver = new IntersectionObserver(
      (entries) => {
        this.footerIntersecting = entries.some((entry) => entry.isIntersecting);
        this.syncFooterState();
      },
      { threshold: 0 }
    );
    this.footerObserver.observe(footer);
  }

  onKeydown(event) {
    // Let an open quick-view modal own its Escape - without this the same keystroke that closes the
    // modal would bubble up and close the fly-out behind it too.
    if (event.target.closest('x-modal')) return;

    if (event.key === 'Escape' && this.classList.contains('is-flyout-open')) {
      this.closeFlyout();
    }
  }

  onClick(event) {
    const addTrigger = event.target.closest('.club-bundle-card__add, .club-bundle-quickview__add');
    if (addTrigger) {
      event.preventDefault();
      const card = addTrigger.closest('[data-bundle-card]');
      if (card) this.addScent(card, addTrigger);
      return;
    }

    const removeTrigger = event.target.closest('[data-bundle-remove-index]');
    if (removeTrigger) {
      event.preventDefault();
      const index = Number(removeTrigger.getAttribute('data-bundle-remove-index'));
      this.removeSelectionAt(index);
      return;
    }

    if (event.target.closest('[data-bundle-panel-close]')) {
      event.preventDefault();
      this.closeFlyout();
      return;
    }

    if (event.target.closest('[data-bundle-panel-tab]')) {
      event.preventDefault();
      this.openFlyout();
      return;
    }

    if (event.target.closest('.club-bundle-builder__submit')) {
      event.preventDefault();
      this.submit();
    }
  }

  /**
   * @param {{ moveFocus?: boolean }} options
   *   moveFocus - whether to send focus to the panel's close button. False for an open the shopper did not
   *   just ask for (the deferred open below), because that one is driven by SCROLLING: taking focus there
   *   would pull it out of whatever the shopper is actually using - the footer's newsletter field,
   *   mid-typing, dismissing the soft keyboard - with no action on their part. Focus only ever follows a
   *   gesture, or a hand-back from the anchor this component parked it on itself.
   */
  openFlyout({ moveFocus = true } = {}) {
    this.classList.add('is-flyout-open');
    this.panelTab?.setAttribute('aria-expanded', 'true');
    if (moveFocus) this.panelCloseButton?.focus();
  }

  /**
   * ECSR-678: open the fly-out, or arrange for it to open as soon as it can actually be seen.
   *
   * Completing a bundle auto-opens the fly-out, and the last row of the grid sits directly above the
   * footer - so the shopper's final "Add to bundle" tap is regularly made with the footer in view. Opening
   * into that state would slide the panel straight off screen (the .is-flyout-open.is-footer-visible rule)
   * while `.is-flyout-open` simultaneously hides the "Your Bundle" tab, leaving nothing on screen to tap:
   * no confirmation, no Add Bundle to Cart, no way back in without scrolling up. That is worse than what
   * this release set out to fix.
   *
   * Making the tab visible instead is not the answer - the tab hiding at the footer is ECSR-758, shipped
   * and signed off, and the client asked for the open cart to "slide away like the bar does", i.e. for
   * BOTH to be gone there. So the open is deferred: the state is remembered and syncFooterState() performs
   * it the moment the footer leaves, which is the first moment there is anything to show.
   */
  requestFlyout() {
    if (this.mobileMedia.matches && this.classList.contains('is-footer-visible')) {
      this.pendingFlyoutOpen = true;
      return;
    }

    this.openFlyout();
  }

  /**
   * ECSR-678: a pending open is meaningless once the desktop layout is in force - neither the fly-out nor
   * its tab exists there - so a tablet rotated to >=1000px drops it, rather than setting `.is-flyout-open`
   * and aria-expanded="true" on controls that are display: none.
   */
  discardPendingFlyoutOnDesktop() {
    if (!this.mobileMedia.matches) this.pendingFlyoutOpen = false;
  }

  closeFlyout() {
    // ECSR-678: the shopper has said they do not want it, so a deferred open must not spring it back open
    // when they scroll away from the footer.
    this.pendingFlyoutOpen = false;
    this.classList.remove('is-flyout-open');
    this.panelTab?.setAttribute('aria-expanded', 'false');

    // ECSR-758: the tab is hidden while the footer is in view, so it is not a valid focus target then - and
    // nothing inside the panel is either, since the closed fly-out is visibility: hidden. Fall back to the
    // builder's end-of-section focus anchor so keyboard/AT focus never drops to <body>.
    if (this.classList.contains('is-footer-visible')) {
      this.focusAnchor();
    } else {
      this.panelTab?.focus();
    }
  }

  /**
   * ECSR-758: programmatic focus anchor for the moments when the only mobile control (the tab) is hidden.
   * The anchor is a visually-hidden, labelled sentinel rendered as the builder's LAST child, so assistive tech
   * announces where focus went and the next Tab continues forward into the footer (where the shopper is
   * looking) rather than restarting at the first grid button.
   */
  focusAnchor() {
    this.querySelector('[data-bundle-focus-anchor]')?.focus({ preventScroll: true });
  }

  /**
   * ECSR-758: the tab is hidden only while the footer is in view AND the mobile layout is active.
   * ECSR-678 (client review callout 3): the OPEN fly-out is slid away in the same state, so whichever of
   * the two currently holds focus has to hand it to the end-of-builder anchor before it is hidden - and get
   * it back when the footer leaves. Which one gets it back depends on what is on screen then: the panel's X
   * if the fly-out is still open, otherwise the tab.
   */
  syncFooterState() {
    this.discardPendingFlyoutOnDesktop();
    const footerVisible = this.mobileMedia.matches && this.footerIntersecting;
    const anchor = this.querySelector('[data-bundle-focus-anchor]');
    const active = document.activeElement;

    if (footerVisible) {
      // Park focus BEFORE the class hides the element holding it, so the browser does not reset it to
      // <body>, and remember which control to hand it back to.
      if (this.panelTab?.contains(active)) {
        this.focusAnchor();
        this.focusParkedFromTab = true;
      } else if (this.classList.contains('is-flyout-open') && this.panel?.contains(active)) {
        this.focusAnchor();
        this.focusParkedFromPanel = true;
      }
    }

    this.classList.toggle('is-footer-visible', footerVisible);

    // ECSR-678: an open deferred at the footer (see requestFlyout) happens now - the panel is on screen
    // again, so there is finally something for the shopper to see. Focus moves ONLY if it is still parked
    // on this component's own anchor; if the shopper has since put it somewhere real, scrolling must not
    // take it back. Deliberately falls through to the hand-back block below so the park flags are resolved
    // by their one owner rather than left stale.
    if (!footerVisible && this.pendingFlyoutOpen) {
      this.pendingFlyoutOpen = false;
      this.openFlyout({ moveFocus: anchor != null && document.activeElement === anchor });
    }

    if (!footerVisible && (this.focusParkedFromTab || this.focusParkedFromPanel)) {
      // The footer has left and the parked-from control is visible again (class removed above, so focus()
      // is not a no-op): return focus to it if it is still parked on the anchor. The fly-out may have been
      // closed while it was off screen, in which case the tab is what the shopper can see.
      const parkedFromPanel = this.focusParkedFromPanel;
      this.focusParkedFromTab = false;
      this.focusParkedFromPanel = false;

      if (anchor && document.activeElement === anchor) {
        const target =
          parkedFromPanel && this.classList.contains('is-flyout-open') ? this.panelCloseButton : this.panelTab;
        target?.focus({ preventScroll: true });
      }
    }
  }

  cardFor(variantId) {
    const exact = this.grid?.querySelector(`[data-bundle-card][data-variant-id="${variantId}"]`);
    if (exact) return exact;

    // ECSR-753: a card's data-variant-id is now the club-size variant, so an Edit link built from an older
    // cart line (or a different size) may carry another variant of the same product. Fall back to the card
    // whose product owns that variant, so the shopper's picks still pre-fill instead of silently vanishing.
    const cards = this.grid?.querySelectorAll('[data-bundle-card][data-variant-ids]') || [];
    for (const card of cards) {
      if (card.dataset.variantIds.split(',').includes(String(variantId))) return card;
    }
    return null;
  }

  addScent(card, triggerButton) {
    if (this.selections.length >= this.bundleSize) return;

    const variantId = Number(card.dataset.variantId);
    if (!variantId) return;

    const alreadyChosen = this.selections.some((selection) => selection.variantId === variantId);
    if (alreadyChosen && !this.allowDuplicates) return;

    this.selections.push({
      variantId,
      name: card.dataset.scentName || '',
      size: card.dataset.sizeLabel || '',
      imageUrl: card.dataset.imageUrl || '',
    });

    // Close the quick-view modal if the "Add to bundle" click came from inside it.
    const modal = triggerButton.closest('x-modal');
    if (modal) {
      modal.dispatchEvent(new CustomEvent('dialog:force-close', { bubbles: false }));
    }

    this.render();

    // ECSR-683: reaching the required count on mobile slides the faux builder cart up from the bottom.
    // When the add came from inside the quick view, wait for the dialog to finish hiding first - its
    // focus trap restores focus on deactivate and would otherwise yank focus back out of the fly-out.
    if (this.selections.length === this.bundleSize && this.mobileMedia.matches) {
      if (modal) {
        modal.addEventListener(
          'dialog:after-hide',
          () => {
            this.requestFlyout();

            // The dialog's focus trap returns focus to the quick-view trigger on a timer AFTER
            // after-hide fires; the open's own focus() has already dispatched its focusin by the
            // time this listener registers, so the one-shot catches the trap's restore and pulls
            // focus back to the X deterministically.
            // Disarmed after a short window so it can never fire on an unrelated focus change if the trap's
            // restore is ever skipped.
            //
            // ECSR-678: skipped entirely when the open was deferred at the footer, because the X is then a
            // visibility: hidden element and focus() on it is a no-op - it would leave focus on the
            // quick-view trigger while claiming to have moved it. syncFooterState() handles focus when it
            // performs the deferred open.
            if (this.pendingFlyoutOpen) return;

            const focusGuard = new AbortController();
            document.addEventListener('focusin', () => this.panelCloseButton?.focus(), { once: true, signal: focusGuard.signal });
            setTimeout(() => focusGuard.abort(), 500);
          },
          { once: true }
        );
      } else {
        this.requestFlyout();
      }
    }
  }

  removeSelectionAt(index) {
    if (index < 0 || index >= this.selections.length) return;
    this.selections.splice(index, 1);
    this.render();

    // render() rebuilds the list's innerHTML, destroying the button that had focus - keep keyboard/AT
    // focus in the panel: the remove button now at this index (or the last one), else the count line.
    const removeButtons = this.listEl?.querySelectorAll('[data-bundle-remove-index]');
    if (removeButtons && removeButtons.length > 0) {
      removeButtons[Math.min(index, removeButtons.length - 1)].focus();
    } else if (this.countEl) {
      this.countEl.setAttribute('tabindex', '-1');
      this.countEl.focus();
    }
  }

  /**
   * Reads ?bundle_edit=<variantId>,<variantId>,... from the URL (built by the cart line's Edit link from the
   * hidden _scent_variant_ids property) and pre-selects any of those variants that still exist in this club's
   * grid. Variants that no longer exist in the collection (discontinued scent) are silently skipped.
   * Also reads ?bundle_uid=<uid> (ECSR-678), which names the single cart line this edit replaces.
   */
  prefillFromQueryString() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(EDIT_QUERY_PARAM);
    if (!raw) return;

    // ECSR-678: this load came from a cart line's Edit link, so a submit REPLACES rather than adds. The
    // uid names which line; a legacy link carries none and findBundleLineToReplace() resolves it from the
    // cart instead. Both are edits - only `isEditing` decides whether anything is replaced at all.
    this.isEditing = true;
    this.editBundleUid = params.get(EDIT_UID_QUERY_PARAM) || null;

    raw
      .split(',')
      .map((value) => Number(value.trim()))
      .filter(Boolean)
      .slice(0, this.bundleSize)
      .forEach((requestedVariantId) => {
        const card = this.cardFor(requestedVariantId);
        if (!card) return; // scent no longer available in this club's collection - can't pre-fill it

        // Always record the card's own (club-size) variant, even when the Edit link carried another variant
        // of the same product - see cardFor().
        const variantId = Number(card.dataset.variantId);
        const alreadyChosen = this.selections.some((selection) => selection.variantId === variantId);
        if (alreadyChosen && !this.allowDuplicates) return;
        if (this.selections.length >= this.bundleSize) return;

        this.selections.push({
          variantId,
          name: card.dataset.scentName || '',
          size: card.dataset.sizeLabel || '',
          imageUrl: card.dataset.imageUrl || '',
        });
      });
  }

  render({ preserveError = false } = {}) {
    // "Your bundle" list
    if (this.listEl) {
      // ECSR-680: each selection shows a mini image, the name with the size directly under it, and a
      // remove control. No prices or quantity selectors ever render here.
      this.listEl.innerHTML = this.selections
        .map((selection, index) => `
          <li class="club-bundle-builder__selection h-stack gap-3 align-center justify-between">
            <div class="h-stack gap-3 align-center">
              ${selection.imageUrl ? `<img class="club-bundle-builder__selection-image" src="${escapeHtml(selection.imageUrl)}" alt="" width="48" height="48" loading="lazy">` : ''}
              <div class="v-stack">
                <span class="text-sm">${escapeHtml(selection.name)}</span>
                ${selection.size ? `<span class="text-xs text-subdued">${escapeHtml(selection.size)}</span>` : ''}
              </div>
            </div>
            <button type="button" class="club-bundle-builder__remove link text-xs" data-bundle-remove-index="${index}" aria-label="${escapeHtml(this.dataset.removeLabel || 'Remove')} ${escapeHtml(selection.name)}">
              ${escapeHtml(this.dataset.removeLabel || 'Remove')}
            </button>
          </li>
        `)
        .join('');
    }

    if (this.countEl) {
      this.countEl.textContent = `${this.selections.length} / ${this.bundleSize} selected`;
    }

    // ECSR-683: the tab mirrors the count so mobile shoppers see each add land while the fly-out is
    // closed, and the sr-only live region announces it (the panel's own live message sits in a
    // visibility:hidden subtree on mobile while closed, so it can't announce there).
    if (this.tabCountEl) {
      this.tabCountEl.textContent = ` (${this.selections.length}/${this.bundleSize})`;
    }

    if (this.liveRegion && this.mobileMedia.matches && !this.classList.contains('is-flyout-open')) {
      // ECSR-755: after a successful add the fly-out has just closed and the cart drawer is opening. The
      // drawer takes no focus and has no live region of its own, so announce the add here instead of a
      // stale "N / N selected" for the builder just left.
      if (this.announceOnNextRender) {
        // ECSR-678: a specific outcome to announce (today: the partial success where the add landed but the
        // line it replaced could not be removed) takes precedence over the generic added message.
        this.liveRegion.textContent = this.announceOnNextRender;
        this.announceOnNextRender = null;
        this.announceAddedOnNextRender = false;
      } else if (this.announceAddedOnNextRender) {
        this.announceAddedOnNextRender = false;
        this.liveRegion.textContent = this.dataset.addedMessage || 'Bundle added to your cart.';
      } else {
        this.liveRegion.textContent = `${this.selections.length} / ${this.bundleSize} selected`;
      }
    }

    const isComplete = this.selections.length === this.bundleSize;

    if (this.messageEl) {
      if (isComplete) {
        this.messageEl.textContent = this.dataset.completeMessage || 'Your bundle is ready to add to cart.';
      } else {
        const remaining = this.bundleSize - this.selections.length;
        const template = this.dataset.incompleteMessage || 'Choose {remaining} more scent(s) to complete your bundle.';
        this.messageEl.textContent = template.replace('{remaining}', remaining);
      }
    }

    // Disable/enable card + quick-view "Add to bundle" buttons for scents that can no longer be chosen.
    this.grid?.querySelectorAll('[data-bundle-card]').forEach((card) => {
      // ECSR-753: sold-out products are no longer rendered at all, so every card here is purchasable.
      const variantId = Number(card.dataset.variantId);
      const chosenCount = this.selections.filter((selection) => selection.variantId === variantId).length;
      const blockedByDuplicateRule = chosenCount > 0 && !this.allowDuplicates;
      const blockedByCapacity = isComplete;
      const shouldDisable = blockedByDuplicateRule || blockedByCapacity;

      card.querySelectorAll('.club-bundle-card__add, .club-bundle-quickview__add').forEach((button) => {
        button.disabled = shouldDisable;
      });

      // State hook only - carries no visual treatment since ECSR-684 removed the selected-state border.
      card.classList.toggle('is-in-bundle', chosenCount > 0);
    });

    if (this.submitButton) {
      this.submitButton.disabled = !isComplete || this.isSubmitting;
    }

    // Any selection change clears a previous add-to-cart error; the submit path's own re-render after a
    // failure passes preserveError so the message it just wrote is not wiped in the same tick.
    if (this.errorEl && !preserveError) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  }

  /**
   * ECSR-678: tell the rest of the theme the cart changed, and close the fly-out on the way.
   *
   * Shared by the success path and by the partial success in submit() where the add landed but the removal
   * it was paired with did not. Both MUST run this: the cart has been written to, and a cart write the
   * theme is never told about leaves the header count and the drawer showing a cart that no longer exists.
   *
   * Wrapped so a failure fetching cart.js still reaches dispatchCartRefresh() - that is the cart drawer's
   * own self-healing path (it re-fetches the section itself), so it is the one call that must not be
   * skipped.
   */
  async finishCartUpdate(sections, { announce } = {}) {
    try {
      const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      cartContent.sections = sections;

      // ECSR-755: on mobile the faux builder cart is a fly-out layered over the page. Close it before the
      // cart drawer opens so the shopper lands on the standard minicart, exactly like any other
      // add-to-cart, instead of the fly-out staying up behind/over it. closeFlyout() hands focus to the
      // "Your Bundle" tab (or, while the footer is in view and the tab hidden, to the builder's focus
      // anchor) - the cart drawer does not take focus itself on touch devices, so this keeps keyboard/AT
      // focus on a visible control.
      if (this.mobileMedia.matches && this.classList.contains('is-flyout-open')) {
        this.closeFlyout();
        // ECSR-678: `announce` overrides the plain "Bundle added to your cart." for a partial success -
        // announcing an unqualified success there would tell an assistive-tech user the opposite of what
        // happened, and is what would send them back to the button to add a third bundle.
        this.announceOnNextRender = announce || null;
        this.announceAddedOnNextRender = !announce;
      }

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cartContent } })
      );
    } catch (error) {
      console.error('Bundle cart write succeeded but the cart could not be re-read:', error);
    } finally {
      dispatchCartRefresh();
    }
  }

  buildProperties(bundleUid) {
    const properties = {
      _bundle_item: 'true',
      _club_handle: this.clubHandle,
      _builder_page_url: this.builderPageUrl,
      _scent_variant_ids: this.selections.map((selection) => selection.variantId).join(','),
      // ECSR-678: keeps this bundle its own cart line even when an identical assortment is added again.
      _bundle_uid: bundleUid,
    };

    this.selections.forEach((selection, index) => {
      const label = selection.size ? `${selection.name} (${selection.size})` : selection.name;
      properties[`Scent ${index + 1}`] = label;
    });

    return properties;
  }

  /**
   * ECSR-678: find the ONE bundle line this builder was opened to edit. Deliberately never matched on the
   * Parent Bundle Product's variant id alone: a shopper may now hold several bundles from the same club,
   * and a variant match would hit an arbitrary one of them.
   *
   * Two ways a line is identified, in order:
   *
   *   1. By the `_bundle_uid` on its Edit link - the normal case for anything added since ECSR-678.
   *   2. LEGACY CARTS. A line added before ECSR-678 has no uid, so its Edit link carries none. Returning
   *      null there would ADD a second bundle and leave the original, i.e. an edit would silently double
   *      what the shopper is charged - a cart persists for around two weeks, so this is a real path
   *      across the deploy, not a theoretical one. It does not need guessing either: the code being
   *      replaced enforced exactly one bundle line per club, so a legacy line is uniquely identified by
   *      its parent variant plus the absence of a uid. Matched only when that yields exactly ONE line;
   *      anything else is ambiguous and falls through to adding a new bundle.
   *
   * Returns null when there is no edit in progress, or when the line named is no longer in the cart (the
   * shopper removed that bundle in another tab) - both of which correctly add a new bundle.
   */
  async findBundleLineToReplace() {
    if (!this.isEditing) return null;

    const response = await fetch(`${window.Shopify.routes.root}cart.js`);
    const cart = await response.json();

    if (this.editBundleUid) {
      return cart.items.find((item) => item.properties?._bundle_uid === this.editBundleUid) || null;
    }

    const legacyLines = cart.items.filter(
      (item) =>
        item.id === this.parentVariantId &&
        item.properties?._bundle_item === 'true' &&
        // Implied by the parent variant today, since each club has its own - stated anyway so the filter
        // cannot silently span two clubs if two builder pages were ever given the same parent product.
        item.properties?._club_handle === this.clubHandle &&
        !item.properties?._bundle_uid
    );

    return legacyLines.length === 1 ? legacyLines[0] : null;
  }

  /**
   * Remove one cart line by key.
   *
   * ECSR-678: `sections` is requested here as well as on the add, because on an edit it is THIS response
   * that reflects the cart the shopper should be shown. The add's section HTML is rendered from a cart that
   * still contains the line being replaced, so using it would flash both bundles and a doubled subtotal in
   * the drawer until the follow-up refresh landed. See submit().
   *
   * Throws on a non-ok response: after the reordering in submit() a silently failed removal leaves the
   * shopper holding the old AND the edited bundle, i.e. charged twice, which must surface as an error
   * rather than be swallowed.
   */
  async removeLine(lineKey, sections = []) {
    const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: sections.join(','),
        sections_url: this.builderPageUrl,
      }),
    });

    if (!response.ok) {
      // Prefer Shopify's own description, as the add path does - "Cannot find variant" is diagnosable,
      // the generic message is not.
      let description = '';
      try {
        const errorJson = await response.json();
        description = errorJson?.description || errorJson?.message || '';
      } catch (error) {
        // Non-JSON error body - fall back to the generic message.
      }
      throw new Error(description || this.dataset.genericErrorMessage || 'Something went wrong. Please try again.');
    }

    return response.json();
  }

  async submit() {
    if (this.isSubmitting) return;
    if (this.selections.length !== this.bundleSize || !this.parentVariantId) return;

    this.isSubmitting = true;
    this.submitButton?.setAttribute('aria-busy', 'true');
    if (this.submitButton) this.submitButton.disabled = true;

    // A message left by a previous failed attempt must not outlive this attempt's outcome.
    if (this.errorEl) {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
    if (this.noticeEl) {
      this.noticeEl.hidden = true;
      this.noticeEl.textContent = '';
    }

    // ECSR-678: minted before the request so the uid that goes on the line is the uid this submit owns,
    // even if the shopper starts another add while this one is in flight.
    const bundleUid = mintBundleUid();

    try {
      // ECSR-678: an EDIT replaces the one line it came from; a plain add never removes anything, so a
      // shopper can hold as many bundles from this club as they like, identical assortments included.
      //
      // Resolved BEFORE the add but removed AFTER it. The ECSR-302 code removed first, which meant a
      // failed add - parent product sells out between the two calls, connection drops - left the shopper
      // with no bundle at all and only a generic error. Adding first means the worst case is that the
      // replacement never happens and the original bundle is still there, which is recoverable; the
      // shopper briefly holds two lines, and nothing renders the cart between the two calls.
      //
      // This ordering DEPENDS on the Parent Bundle Products not tracking inventory (all four are
      // `inventoryItem.tracked: false`, verified against the Admin API for ECSR-678). If tracking is ever
      // switched on for one of them, the add here runs while the line it replaces still holds its unit, so
      // editing the last available bundle would be refused at the inventory ceiling while plain adds keep
      // working. Revisit the ordering if that changes.
      const lineToReplace = await this.findBundleLineToReplace();

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          id: this.parentVariantId,
          quantity: 1,
          properties: this.buildProperties(bundleUid),
          sections: sectionsToBundle.join(','),
          sections_url: `${window.Shopify.routes.root}variants/${this.parentVariantId}`,
        }),
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.description || responseJson.message || this.dataset.genericErrorMessage);
      }

      // The add landed, so the line it replaces can go. Its key is still valid: /cart/add.js appends a new
      // line rather than reordering existing ones, and /cart/change.js by key is index-independent.
      // The removal's own section HTML is what the drawer is rendered from - see removeLine().
      let sectionsForRender = responseJson.sections;
      if (lineToReplace) {
        try {
          const removalJson = await this.removeLine(lineToReplace.key, sectionsToBundle);
          if (removalJson?.sections) sectionsForRender = removalJson.sections;
        } catch (removalError) {
          // PAST THE POINT OF NO RETURN: the add succeeded, so the cart HAS changed. Letting this throw to
          // the outer catch would skip the cart refresh and leave the edit armed - the shopper would see
          // only "something went wrong", no cart change at all, and tapping the button again would add yet
          // another bundle. So this is finished as a partial success rather than reported as a failure.
          //
          // Reachable: the line resolved a round-trip ago is gone by now because the shopper removed that
          // bundle in the drawer they left open, or in another tab.
          console.error('Bundle added, but the line it replaces could not be removed:', removalError);

          // The edit is spent either way - the add is done and cannot be repeated. What is left behind is
          // just an ordinary bundle line the shopper can remove themselves.
          this.isEditing = false;
          this.editBundleUid = null;

          // Say what actually happened, so nobody retries an operation that already took effect.
          //
          // NOT the panel's error container: this path ends with the fly-out closed on mobile, which puts
          // that container at visibility: hidden - the shopper would never see it. The notice element sits
          // outside the panel for exactly this reason, and the message is also what the live region
          // announces, in place of the plain "added" announcement that would otherwise contradict it.
          const partialMessage =
            this.dataset.replaceFailedMessage ||
            'Your new bundle was added to the cart, but the bundle it was replacing could not be removed. Please remove it from your cart.';

          if (this.noticeEl) {
            this.noticeEl.hidden = false;
            this.noticeEl.textContent = partialMessage;
          }

          // The add's own section HTML is accurate here precisely BECAUSE the removal did not happen, so the
          // drawer renders a cart that matches reality.
          await this.finishCartUpdate(responseJson.sections, { announce: partialMessage });
          return; // `finally` still runs
        }
      }

      // ECSR-678: the edit is spent. The line it named no longer exists, so a further tap of Add Bundle to
      // Cart is a NEW bundle, not another attempt to replace something. Cleared only once the cart write has
      // actually happened - a failed ADD leaves the edit in place so retrying still replaces the right line.
      this.isEditing = false;
      this.editBundleUid = null;

      await this.finishCartUpdate(sectionsForRender);
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      if (this.errorEl) {
        this.errorEl.hidden = false;
        this.errorEl.textContent = error.message || this.dataset.genericErrorMessage || 'Something went wrong. Please try again.';
      }
    } finally {
      this.isSubmitting = false;
      this.submitButton?.removeAttribute('aria-busy');
      this.render({ preserveError: true });
    }
  }
}

/**
 * ECSR-678: a unique id per bundle add. Its only job is to make two otherwise identical adds distinct
 * line items, so it needs uniqueness, not unguessability. crypto.randomUUID is not available on
 * http origins or on older Safari, hence the counter+time fallback.
 */
let bundleUidCounter = 0;
function mintBundleUid() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (error) {
    // Fall through to the deterministic fallback below.
  }
  bundleUidCounter += 1;
  return `b-${Date.now().toString(36)}-${bundleUidCounter}-${Math.random().toString(36).slice(2, 10)}`;
}

function escapeHtml(value) {
  // Also escape quotes so the result is safe inside quoted attribute values, not just text nodes.
  const div = document.createElement('div');
  div.textContent = value == null ? '' : String(value);
  return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

if (!window.customElements.get('club-bundle-builder')) {
  window.customElements.define('club-bundle-builder', ClubBundleBuilder);
}
